import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Loader2, Send, FlaskConical } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { invokeLeadAgentSimulateTurn } from '../../lib/aiAgents';

type ChatLine = { role: string; content: string; timestamp?: string };

interface LeadAgentSimulateModalProps {
  open: boolean;
  onClose: () => void;
  agentId: string;
  businessId: string;
  agentName: string;
}

/** Strip inbound [SYSTEM] wrapper so the customer column shows only what a lead typed. */
function customerFacingUserText(raw: string): string {
  const t = raw.trimStart();
  if (t.startsWith('[SYSTEM]')) {
    const m = raw.match(/\bTheir message:\s*([\s\S]*)/i);
    if (m?.[1]) return m[1].trim();
  }
  return raw;
}

/** CRM kickoff line — not shown as a customer SMS on the left. */
function isInitialAssignKickoffUserRow(raw: string): boolean {
  return /^\[SYSTEM\]\s*Initial assignment:/i.test(raw.trimStart());
}

export default function LeadAgentSimulateModal({
  open,
  onClose,
  agentId,
  businessId,
  agentName,
}: LeadAgentSimulateModalProps) {
  const [messages, setMessages] = useState<ChatLine[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);

  const customerThreadMessages = useMemo(
    () => messages.filter((m) => !(m.role === 'user' && isInitialAssignKickoffUserRow(m.content))),
    [messages],
  );

  const fetchSessionFromDb = useCallback(async (): Promise<{ sessionId: string | null; messages: ChatLine[] }> => {
    const { data: userData } = await supabase.auth.getUser();
    const uid = userData?.user?.id;
    if (!uid) return { sessionId: null, messages: [] };
    const { data, error } = await supabase
      .from('ai_lead_agent_simulation_sessions')
      .select('id, messages')
      .eq('business_id', businessId)
      .eq('agent_id', agentId)
      .eq('user_id', uid)
      .maybeSingle();
    if (error || !data) return { sessionId: null, messages: [] };
    return {
      sessionId: data.id,
      messages: Array.isArray(data.messages) ? data.messages : [],
    };
  }, [agentId, businessId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const { sessionId: sid, messages: loaded } = await fetchSessionFromDb();
        if (cancelled) return;
        setSessionId(sid);
        setMessages(loaded);
        if (loaded.length === 0) {
          const res = await invokeLeadAgentSimulateTurn({
            agentId,
            businessId,
            message: '',
            simulationSessionId: sid ?? undefined,
            runMode: 'initial_assign',
          });
          if (cancelled) return;
          if (res.success && res.messages) {
            setMessages(res.messages as ChatLine[]);
            if (res.simulation_session_id) setSessionId(res.simulation_session_id);
          } else if (!res.success) {
            window.alert(res.error || 'Could not start simulation');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, agentId, businessId, fetchSessionFromDb]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setInput('');
    try {
      const res = await invokeLeadAgentSimulateTurn({
        agentId,
        businessId,
        message: trimmed,
        simulationSessionId: sessionId,
      });
      if (!res.success) {
        setInput(trimmed);
        window.alert(res.error || 'Simulation failed');
        return;
      }
      if (res.simulation_session_id) setSessionId(res.simulation_session_id);
      if (res.messages) setMessages(res.messages as ChatLine[]);
    } finally {
      setSending(false);
    }
  };

  const handleReset = async () => {
    if (!sessionId && messages.length === 0) return;
    if (!window.confirm('Clear this simulation thread?')) return;
    setResetting(true);
    try {
      if (sessionId) {
        const { error } = await supabase.from('ai_lead_agent_simulation_sessions').delete().eq('id', sessionId);
        if (error) {
          window.alert(error.message);
          return;
        }
      }
      setSessionId(null);
      setMessages([]);
      const res = await invokeLeadAgentSimulateTurn({
        agentId,
        businessId,
        message: '',
        runMode: 'initial_assign',
      });
      if (res.success && res.messages) {
        setMessages(res.messages as ChatLine[]);
        if (res.simulation_session_id) setSessionId(res.simulation_session_id);
      } else if (!res.success) {
        window.alert(res.error || 'Could not restart simulation');
      }
    } finally {
      setResetting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[12000] flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <div className="flex items-start gap-2 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
              <FlaskConical className="w-5 h-5 text-violet-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-gray-900">Simulate lead agent</h2>
              <p className="text-xs text-gray-500 mt-0.5 leading-snug">
                Same <span className="font-semibold text-gray-700">ai-lead-agent-turn</span> as a real assignment,
                including an <strong className="text-gray-700">initial_assign</strong> first message when the thread is
                empty (like a new lead with no details). The left column is the <strong className="text-gray-700">customer&apos;s phone</strong>; the right shows the{' '}
                <strong className="text-gray-700">full thread</strong> the model and CRM would see.
              </p>
              <p className="text-[11px] text-violet-700 font-medium mt-1 truncate">Agent: {agentName}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-2 border-b border-gray-50 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => void handleReset()}
            disabled={resetting || loading || (!sessionId && messages.length === 0)}
            className="text-xs font-medium text-amber-700 hover:text-amber-900 disabled:opacity-40"
          >
            {resetting ? 'Clearing…' : 'Clear simulation'}
          </button>
          {sessionId ? (
            <span className="text-[10px] text-gray-400 font-mono truncate max-w-[180px]" title={sessionId}>
              session {sessionId.slice(0, 8)}…
            </span>
          ) : null}
        </div>

        <div className="flex flex-1 min-h-0 flex-col md:flex-row md:min-h-[min(420px,calc(92vh-200px))]">
          {/* Customer phone */}
          <div className="flex flex-1 flex-col min-h-0 min-w-0 border-b md:border-b-0 md:border-r border-gray-200">
            <div className="px-3 py-1.5 bg-emerald-50/95 border-b border-emerald-100/80 text-[11px] font-semibold text-emerald-900">
              Your phone (customer)
            </div>
            <div className="flex-1 min-h-[160px] max-h-[38vh] md:max-h-none overflow-y-auto px-4 py-3 space-y-3 bg-white">
              {loading ? (
                <div className="flex flex-col items-center justify-center gap-2 py-10 text-xs text-gray-500">
                  <Loader2 className="w-7 h-7 text-emerald-600 animate-spin" />
                  <span>Loading or running first assignment…</span>
                </div>
              ) : customerThreadMessages.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-8 px-2 leading-relaxed">
                  The agent should text you first. If nothing appears, check SMS is enabled on this agent and try
                  Clear simulation.
                </p>
              ) : (
                customerThreadMessages.map((m, i) => {
                  const isUser = m.role === 'user';
                  const display = isUser ? customerFacingUserText(m.content) : m.content;
                  return (
                    <div
                      key={`${m.timestamp || i}-${i}`}
                      className={`flex flex-col gap-0.5 ${isUser ? 'items-end' : 'items-start'}`}
                    >
                      <span
                        className={`text-[10px] font-medium uppercase tracking-wide ${
                          isUser ? 'text-emerald-700/90' : 'text-gray-500'
                        }`}
                      >
                        {isUser ? 'You' : agentName}
                      </span>
                      <div
                        className={`text-sm rounded-2xl px-3 py-2 max-w-[min(100%,20rem)] whitespace-pre-wrap leading-snug ${
                          isUser
                            ? 'bg-emerald-600 text-white shadow-sm'
                            : 'bg-gray-100 text-gray-900 border border-gray-200/80 shadow-sm'
                        }`}
                      >
                        {display}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="p-4 border-t border-gray-100 flex gap-2 bg-gray-50/60">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void handleSend();
                  }
                }}
                placeholder="Message as the customer (plain text)…"
                rows={2}
                disabled={sending || loading}
                className="flex-1 text-sm border border-gray-300 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 bg-white"
              />
              <button
                type="button"
                onClick={() => void handleSend()}
                disabled={sending || loading || !input.trim()}
                className="self-end h-11 w-11 flex items-center justify-center rounded-xl bg-emerald-600 text-white disabled:opacity-40 shadow-sm"
              >
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Business / CRM raw thread */}
          <div className="flex w-full shrink-0 flex-col md:w-[min(100%,320px)] min-h-[140px] max-h-[36vh] md:max-h-none bg-slate-50/90">
            <div className="px-3 py-1.5 border-b border-slate-200/80 bg-slate-100/90 text-[11px] font-semibold text-slate-800">
              CRM / model thread (business)
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {loading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <p className="text-[10px] text-slate-500 leading-relaxed">
                  Full transcript for the model: <code className="rounded bg-white px-0.5 border border-slate-200">[SYSTEM]</code>{' '}
                  initial assign and inbound SMS framing appear here.
                </p>
              ) : (
                messages.map((m, i) => (
                  <div
                    key={`raw-${m.timestamp || i}-${i}`}
                    className="rounded-lg border border-slate-200/80 bg-white p-2 shadow-sm"
                  >
                    <div className="mb-1 flex items-center gap-1.5">
                      <span
                        className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${
                          m.role === 'user'
                            ? 'bg-violet-100 text-violet-800'
                            : m.role === 'assistant'
                              ? 'bg-slate-200 text-slate-800'
                              : 'bg-amber-100 text-amber-900'
                        }`}
                      >
                        {m.role}
                      </span>
                    </div>
                    <pre className="whitespace-pre-wrap break-words font-mono text-[10px] leading-relaxed text-slate-800">
                      {m.content}
                    </pre>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
